import type { Dispatch, SetStateAction } from 'react';
import type {
  GuidedOpen,
  HotelResultsProps,
  HotelSearchValue,
} from '@voll-intelligence/intelligence-ui-kit';
import type { GenUiDispatch } from '../../../shared/ui/action.js';
import { applyMyLocation } from './hotel_selection.js';
import { hotelSearchDateSummary } from './hotel_planner_view.js';
import type { useHotelSearchOptions } from './use_hotel_search_options.js';

/** Onde o primeiro campo pendente da barra nasce aberto: o card da conversa (o
 *  organismo com `dispatch`) também no celular (#4879), a página determinística
 *  só no palco largo, e o card remontado do histórico nunca — o gesto já foi
 *  gasto. */
export function guidedOpenOf(replay: boolean, dispatch: GenUiDispatch | undefined): GuidedOpen {
  if (replay) return 'never';
  return dispatch ? 'always' : 'wide';
}

/**
 * As props da BARRA de busca (`HotelResultsProps.search`) montadas pelo
 * organismo do hotel — o rascunho, as opções remotas, as travas e o disparo.
 * Fora de `hotel_results_organism.tsx` pelo teto de linhas dele; o organismo é
 * o único consumidor.
 */
export function searchProps({
  value,
  copy,
  locale,
  requesterId,
  options,
  locks,
  busy,
  guidedOpen,
  canSubmit,
  onChange,
  onSubmit,
}: {
  readonly value: HotelSearchValue;
  readonly copy: HotelResultsProps['search']['copy'];
  readonly locale: string;
  readonly requesterId?: string;
  readonly options: ReturnType<typeof useHotelSearchOptions>;
  /** As travas da barra (`resultsLocks` do organismo): inerte, superada (o
   *  submit escreve na conversa em vez de buscar) e com o preenchimento
   *  guiado ligado. */
  readonly locks: {
    readonly searchLocked: boolean;
    readonly searchIsTurn: boolean;
    readonly searchGuided: boolean;
  };
  readonly busy: boolean;
  /** Onde o primeiro campo pendente nasce aberto — o organismo decide pela
   *  superfície (ver `GuidedOpen`). */
  readonly guidedOpen: GuidedOpen;
  /** A régua de "pode buscar" do organismo para um valor da barra. */
  readonly canSubmit: (value: HotelSearchValue) => boolean;
  /** O SETTER do valor da faixa: `applyMyLocation` precisa da forma funcional
   *  para escrever no valor mais novo, não no que o clique capturou. */
  readonly onChange: Dispatch<SetStateAction<HotelSearchValue>>;
  readonly onSubmit: (value: HotelSearchValue) => void;
}): HotelResultsProps['search'] {
  return {
    value,
    copy,
    dateSummary: hotelSearchDateSummary(value, locale),
    ...(value.checkin && value.checkout
      ? { dateAccessibleSummary: `${value.checkin} – ${value.checkout}` }
      : {}),
    ...(requesterId ? { requesterId } : {}),
    travelers: options.travelers,
    placeSuggestions: options.placeSuggestions,
    placeSearchState: options.placeSearchState,
    travelerSearchState: options.travelerSearchState,
    hasMoreTravelers: options.hasMoreTravelers,
    readOnly: locks.searchLocked,
    busy,
    canSubmit,
    submitIsTurn: locks.searchIsTurn,
    guided: locks.searchGuided,
    guidedOpen,
    onChange,
    onSubmit,
    onPlaceQueryChange: options.onPlaceQueryChange,
    onTravelerQueryChange: options.onTravelerQueryChange,
    onLoadMoreTravelers: options.onLoadMoreTravelers,
    myLocation: { state: options.myLocationState },
    onUseMyLocation: () => {
      void options.requestMyLocation().then(applyMyLocation(value, onChange));
    },
    onPlacePick: options.cancelMyLocation,
  };
}
